// src/lib/media/links.ts
//
// Pure, isomorphic link helpers — the normalization layer shared by the Sources
// component (client) and the reconcile script (scripts/reconcile_page_links.mts).
// NO fs, NO process.env — only URL string shaping. On-disk resolution and
// liveness checks live in the reconcile script.
//
// Reuses the YouTube id/thumbnail helpers from schema.ts so there is one source
// of truth for "what is a YouTube url".

import { youtubeId, youtubeThumb } from './schema';

// The site's own hostnames — used to collapse same-site absolute links to
// root-relative and to classify a link as internal.
export const SITE_HOSTS = ['thedailydialectics.com'];

export type LinkKind =
  | 'youtube'
  | 'pdf'
  | 'image'
  | 'internal'
  | 'mailto'
  | 'external';

// Tracking / player-chrome query params that carry no identity — stripped during
// canonicalization and dedup so `embed/ID?wmode=opaque` and `watch?v=ID` collapse.
const JUNK_PARAMS = new Set([
  'wmode', 'feature', 'rel', 'start', 'end', 't', 'index', 'list', 'ab_channel',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'fbclid', 'gclid', 'mc_cid', 'mc_eid', 'ref', 'ref_src',
]);

const IMAGE_EXT = /\.(jpe?g|png|gif|bmp|tiff?|webp|svg|avif|ico)$/i;

export interface YouTubeCanonical {
  id: string;
  watchUrl: string;   // canonical human/clickable form (Sources link)
  embedUrl: string;   // iframe form
  thumb: string;
}

// Any YouTube url/id form -> canonical watch + embed + thumbnail, or null.
// This is the embed <-> watch?v= correction: it discards `?wmode=opaque` & co.
export function canonicalYouTube(input: string): YouTubeCanonical | null {
  const id = youtubeId(stripWrappers(input));
  if (!id) return null;
  return {
    id,
    watchUrl: `https://www.youtube.com/watch?v=${id}`,
    embedUrl: `https://www.youtube.com/embed/${id}`,
    thumb: youtubeThumb(id),
  };
}

// Strip angle-bracket wrappers and trailing prose punctuation that leaks out of
// markdown/HTML when a url is grabbed by a loose regex.
export function stripWrappers(input: string): string {
  let u = (input || '').trim();
  u = u.replace(/^<+/, '').replace(/>+$/, '');
  // Trailing chars that are almost never part of a real url at the very end.
  u = u.replace(/[.,;:'")\]}]+$/, '');
  return u;
}

// Collapse a same-site absolute url to a root-relative path; otherwise return the
// (wrapper-stripped) url unchanged. The url-string half of the internal-link
// reconciler — disk resolution is the script's job.
export function normalizeUrl(input: string, siteHosts: string[] = SITE_HOSTS): string {
  const u = stripWrappers(input);
  if (!u) return u;
  try {
    const p = new URL(u);
    const host = p.hostname.replace(/^www\./, '');
    if (siteHosts.includes(host)) {
      return (p.pathname + p.search + p.hash) || '/';
    }
  } catch {
    /* relative path — already root-relative or bare */
  }
  return u;
}

export function classifyLink(
  input: string,
  siteHosts: string[] = SITE_HOSTS,
): { kind: LinkKind; url: string } {
  const raw = stripWrappers(input);
  if (/^mailto:/i.test(raw)) return { kind: 'mailto', url: raw };

  const yt = canonicalYouTube(raw);
  if (yt) return { kind: 'youtube', url: yt.watchUrl };

  let host = '';
  let path = raw;
  try {
    const p = new URL(raw);
    host = p.hostname.replace(/^www\./, '');
    path = p.pathname;
  } catch {
    /* relative */
  }
  const isInternal = raw.startsWith('/') || (!!host && siteHosts.includes(host));
  const bare = path.split('?')[0].split('#')[0];

  if (/\.pdf$/i.test(bare)) return { kind: 'pdf', url: isInternal ? normalizeUrl(raw, siteHosts) : raw };
  if (IMAGE_EXT.test(bare)) return { kind: 'image', url: isInternal ? normalizeUrl(raw, siteHosts) : raw };
  if (isInternal) return { kind: 'internal', url: normalizeUrl(raw, siteHosts) };
  return { kind: 'external', url: raw };
}

// A scheme- and chrome-insensitive identity for a link, used to dedupe Sources
// cards and to look a link up in the deadlinks list. YouTube collapses to its id
// (so a watch link and an embed link of the same video are one source).
export function dedupeKey(input: string): string {
  const raw = stripWrappers(input);
  const yt = canonicalYouTube(raw);
  if (yt) return `yt:${yt.id}`;
  try {
    const p = new URL(raw);
    for (const k of [...p.searchParams.keys()]) {
      if (JUNK_PARAMS.has(k.toLowerCase())) p.searchParams.delete(k);
    }
    const host = p.hostname.replace(/^www\./, '').toLowerCase();
    const path = p.pathname.replace(/\/+$/, '');
    const q = p.searchParams.toString();
    // scheme-agnostic: http and https of the same resource are one key
    return `${host}${path}${q ? `?${q}` : ''}`.toLowerCase();
  } catch {
    return raw.replace(/[#?].*$/, '').replace(/\/+$/, '').toLowerCase();
  }
}
